'use client'

import { memo, useRef } from "react";
import { motion, useAnimationFrame, useMotionValue } from "framer-motion";
import { Star } from "lucide-react";

export type Testimonial = {
  quote: string;
  name: string;
  title?: string;
};

const ROW_1: Testimonial[] = [
  { quote: "The most refined salon experience I've had in Paris. A genuine atelier.", name: "Camille Aubert", title: "Vogue Paris" },
  { quote: "Eloise transformed my hair without sacrificing a single millimeter of length.", name: "Sienna Hartwell", title: "Editor, Cereal" },
  { quote: "Booking took thirty seconds. The result lasted six months.", name: "Margot Lévesque", title: "Architect" },
  { quote: "Discreet, precise, and deeply considered. Worth every euro.", name: "Isolde Brandt", title: "Gallerist" },
  { quote: "I fly in from Milan twice a year. Nowhere else compares.", name: "Greta Moretti", title: "Creative Director" },
];

const ROW_2: Testimonial[] = [
  { quote: "Auguste's color work is sculpture. Subtle, dimensional, alive.", name: "Beatrice Wynn", title: "Stylist" },
  { quote: "The glow facial gave me skin I haven't had since twenty-two.", name: "Naoko Hidaka", title: "Founder, Kōrin" },
  { quote: "Quiet, intentional, and impossibly chic. My new ritual.", name: "Anouk Devereux", title: "Author" },
  { quote: "Even the waiting area feels like a private suite at the Connaught.", name: "Cordelia Ashford", title: "Patron" },
  { quote: "A masterclass in restraint and craft. I left feeling rewritten.", name: "Lior Bensimon", title: "Producer" },
];

const Stars = memo(() => (
  <div className="flex gap-0.5 text-rose-gold">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className="h-3 w-3 fill-current" strokeWidth={0} />
    ))}
  </div>
));
Stars.displayName = "Stars";

const Card = memo(({ t }: { t: Testimonial }) => (
  <figure className="mx-3 flex h-full w-[320px] shrink-0 flex-col justify-between rounded-sm border border-border/60 bg-card/60 p-6 shadow-soft backdrop-blur-md md:w-[380px]">
    <Stars />
    <blockquote className="mt-4 font-sans text-sm leading-relaxed text-foreground/85">
      &ldquo;{t.quote}&rdquo;
    </blockquote>
    <figcaption className="mt-5 border-t border-border/60 pt-4">
      <p className="font-display text-lg italic leading-none">{t.name}</p>
      {t.title && (
        <p className="mt-1.5 text-[0.65rem] uppercase tracking-editorial text-muted-foreground">
          {t.title}
        </p>
      )}
    </figcaption>
  </figure>
));
Card.displayName = "Card";

type RowProps = {
  items: Testimonial[];
  direction?: "left" | "right";
  speed?: number; // px per second
};

const MarqueeRow = memo(({ items, direction = "left", speed = 50 }: RowProps) => {
  const x = useMotionValue(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const hoverRef = useRef(false);
  const slowdownRef = useRef(1);
  const halfWidthRef = useRef(0);

  useAnimationFrame((_, delta) => {
    const target = hoverRef.current ? 0 : 1;
    // ease the slowdown factor toward target
    slowdownRef.current += (target - slowdownRef.current) * Math.min(1, delta / 350);

    if (!halfWidthRef.current && trackRef.current) {
      halfWidthRef.current = trackRef.current.scrollWidth / 2;
    }
    const half = halfWidthRef.current;
    if (!half) return;

    const move = (speed * delta * slowdownRef.current) / 1000;
    let next = x.get() + (direction === "left" ? -move : move);

    // wrap seamlessly
    if (direction === "left" && next <= -half) next += half;
    if (direction === "right" && next >= 0) next -= half;

    x.set(next);
  });

  // Initialize right-moving row from -half so it starts wrapped
  if (direction === "right" && x.get() === 0 && halfWidthRef.current) {
    x.set(-halfWidthRef.current);
  }

  const loop = [...items, ...items];

  return (
    <div
      className="relative overflow-hidden"
      onMouseEnter={() => (hoverRef.current = true)}
      onMouseLeave={() => (hoverRef.current = false)}
    >
      {/* edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-background to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-background to-transparent" />

      <motion.div ref={trackRef} style={{ x }} className="flex w-max will-change-transform">
        {loop.map((t, i) => (
          <Card key={`${t.name}-${i}`} t={t} />
        ))}
      </motion.div>
    </div>
  );
});
MarqueeRow.displayName = "MarqueeRow";

function TestimonialsMarqueeBase() {
  return (
    <section className="border-y border-border bg-secondary/30 py-20">
      <div className="mx-auto mb-12 max-w-[1600px] px-6 md:px-12">
        <p className="eyebrow">N° 02.5 — In Their Words</p>
        <h2 className="mt-4 max-w-2xl font-display text-4xl md:text-6xl">
          Voices from the <em>chair</em>.
        </h2>
      </div>
      <div className="flex flex-col gap-5">
        <MarqueeRow items={ROW_1} direction="left" speed={45} />
        <MarqueeRow items={ROW_2} direction="right" speed={38} />
      </div>
    </section>
  );
}

export const TestimonialsMarquee = memo(TestimonialsMarqueeBase);
